/**
 * 供应商信息管理工具
 * 统一管理供应商信息的获取、存储和使用
 *
 * ============================================
 * 📚 使用指南
 * ============================================
 * // 示例 1：普通页面获取供应商信息
 * const supplierInfo = await getSupplierInfo(this);
 *
 * // 示例 2：PV 埋点
 * reportPageView(this, 'c_chaoshi_xxx');
 *
 * // 示例 3：模块曝光/点击埋点
 * reportModuleView(this, 'b_chaoshi_xxx_mv', {
 *     city_id: this.cityId, // 只需传自定义参数
 * });
 * reportModuleClick(this, 'b_chaoshi_xxx_mc', {
 *     sku_id: this.skuId,
 * });
 *
 * // 示例 4：快速获取特定字段
 * const supplierId = getSupplierField('supplierId');
 * const phone = getSupplierField('phone');
 */

const STORAGE_KEY = 'globalSupplierInfo';
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24小时过期时间

/**
 * 从缓存获取供应商信息
 * @returns {Object|null}
 */
export function getSupplierInfoFromCache() {
    try {
        const cachedData = localStorage.getItem(STORAGE_KEY);
        if (!cachedData) {
            return null;
        }

        const parsed = JSON.parse(cachedData);

        // 检查缓存是否过期
        if (parsed.timestamp && Date.now() - parsed.timestamp > CACHE_EXPIRY_TIME) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }

        return parsed.data || null;
    } catch (error) {
        // 读取供应商信息缓存失败
        return null;
    }
}

/**
 * 设置供应商信息到缓存
 * @param {Object} supplierInfo
 */
export function setSupplierInfoToCache(supplierInfo) {
    try {
        const cacheData = {
            data: supplierInfo,
            timestamp: Date.now(), // 记录缓存时间
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
        // 存储供应商信息失败
    }
}

/**
 * 根据 supplierId 获取供应商详细信息（supplierCode 和业态列表）
 * @param {Object} vm - Vue 实例
 * @returns {Promise<Object>} 供应商详细信息 { supplierCode, ytTypeList }
 */
async function querySupplierDetailInfo(vm) {
    try {
        const res = await vm.$hPost('/api/vss/second/shepherd/sqm/SupplierInfoThriftService/querySupplierInfoBySupplierIdList');

        // 直接返回结果
        return res || {};
    } catch (error) {
        // 获取供应商详细信息失败
        return {};
    }
}

/**
 * 根据 supplierId 获取供应商分类信息
 * @param {Object} vm - Vue 实例
 * @param {Number} supplierId - 供应商ID
 * @returns {Promise<Object>} 供应商分类信息 { categoryIds }
 */
async function querySupplierCategoryInfo(vm, supplierId) {
    if (!supplierId) {
        return {};
    }

    try {
        const res = await vm.$hGet('/api/vss/second/shepherd/supplier/query/code/detail', {
            supplierId,
        });

        // 直接返回结果
        return res || {};
    } catch (error) {
        // 获取供应商分类信息失败
        return {};
    }
}

/**
 * 校验缓存信息是否完整
 * @param {Object} cachedInfo - 缓存的供应商信息
 * @returns {Boolean} 是否完整
 */
function validateCachedInfo(cachedInfo) {
    if (!cachedInfo) {
        return false;
    }

    // 必需字段列表
    const requiredFields = ['supplierId', 'supplierCode', 'bizType'];

    // 检查是否所有必需字段都存在且有值
    return requiredFields.every((field) => {
        const value = cachedInfo[field];
        // 检查字段存在且不为 null/undefined
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== null && value !== undefined && value !== '';
    });
}

/**
 * 获取供应商信息（优先从缓存读取）
 * @param {Object} vm - Vue 实例
 * @param {Object} options - 请求选项
 * @returns {Promise<Object>} 供应商信息
 */
export async function getSupplierInfo(vm, options = {}) {
    // 1. 优先从 localStorage 读取
    const cachedInfo = getSupplierInfoFromCache();
    // 2. 校验缓存是否完整
    const isCacheValid = validateCachedInfo(cachedInfo);
    if (cachedInfo && isCacheValid && !options.forceRefresh) {
        return cachedInfo;
    }
    // 3. 如果缓存不存在、不完整或强制刷新，则调用接口
    try {
        const rawInfo = await vm.__querySupplierInfoByBizAccountId(options);

        // 4. 初始化 supplierCode、ytTypeList 和 categoryIds
        let supplierCode = '';
        let ytTypeList = [];
        let categoryIds = [];

        // 5. 如果有 supplierId，则并发调用两个接口获取详细信息
        if (rawInfo && rawInfo.supplierId) {
            // 使用 Promise.all 并发调用两个接口
            const [detailInfo, categoryInfo] = await Promise.all([
                querySupplierDetailInfo(vm),
                querySupplierCategoryInfo(vm, rawInfo.supplierId),
            ]);

            // 处理 detailInfo 返回的数据
            if (Array.isArray(detailInfo) && detailInfo.length > 0) {
                supplierCode = detailInfo[0].supplierCode || '';
                ytTypeList = detailInfo[0].ytTypeList || [];
            }

            // 处理 categoryInfo 返回的数据
            if (categoryInfo && categoryInfo.bgCategoryDetailList) {
                categoryIds = categoryInfo.bgCategoryDetailList;
            }
        }
        const supplierInfo = {
            supplierId: rawInfo.supplierId,
            supplierCode,
            supplierName: rawInfo.supplierName,
            bizType: ytTypeList,
            supplierCategoryId: categoryIds,
            phoneNum: rawInfo.phone,
            account: rawInfo.login,
        };
        // 存储到 localStorage
        setSupplierInfoToCache(supplierInfo);
        return supplierInfo;
    } catch (error) {
        // 如果接口失败，尝试返回缓存数据
        return cachedInfo || {};
    }
}

/**
 * 清除供应商信息缓存
 */
export function clearSupplierInfoCache() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        // 清除供应商信息缓存失败
    }
}

/**
 * 获取特定字段（快捷方法）
 * @param {String} field - 字段名，如 'supplierId', 'customerId', 'phone' 等
 * @returns {any}
 */
export function getSupplierField(field) {
    const info = getSupplierInfoFromCache();
    return info ? info[field] : undefined;
}

/**
 * 用于埋点的供应商信息获取
 * @param {Object} vm - Vue 实例
 * @returns {Promise<Object>} 包含常用埋点字段的对象
 */
export async function getSupplierInfoForTracking(vm) {
    const info = await getSupplierInfo(vm);
    return {
        supplier_id: info.supplierId, // 供货规则ID
        supplier_code: info.supplierCode, // 供货规则编码
        supplier_name: info.supplierName, // 供货规则名称
        biz_type: info.bizType, // 账号ID
        supplier_category_id: info.supplierCategoryId, // 业态列表
        VSS_phoneNum: info.phoneNum,
        VSS_account: info.account,
    };
}
